import React from 'react';
import { SparklesIcon, DollarSignIcon, UsersIcon, CalendarIcon } from 'lucide-react';
interface InputParams {
  revenueTarget: number;
  segmentMix: {
    enterprise: number;
    midMarket: number;
    smb: number;
  };
  averageDealSize: {
    enterprise: number;
    midMarket: number;
    smb: number;
  };
  conversionRates: {
    sql_to_opportunity: number;
    opportunity_to_close: number;
  };
}
interface RecommendationsPanelProps {
  params: InputParams;
}
export function RecommendationsPanel({
  params
}: RecommendationsPanelProps) {
  const playMix = [{
    name: 'High-Intent Account Blitz',
    allocation: 35,
    expectedRevenue: 3500000,
    color: 'bg-blue-500'
  }, {
    name: 'PLG Activation',
    allocation: 25,
    expectedRevenue: 2500000,
    color: 'bg-purple-500'
  }, {
    name: 'Executive Nurture',
    allocation: 20,
    expectedRevenue: 2000000,
    color: 'bg-green-500'
  }, {
    name: 'Inbound Lead Scoring',
    allocation: 20,
    expectedRevenue: 2000000,
    color: 'bg-orange-500'
  }];
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2974">
      <div className="flex items-center gap-2 mb-6" data-id="element-2975">
        <SparklesIcon className="w-5 h-5 text-red-600" data-id="element-2976" />
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-2977">
          AI-Powered Recommendations
        </h2>
      </div>
      {/* Recommended Play Mix */}
      <div className="mb-6" data-id="element-2978">
        <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-2979">
          Suggested Play Mix
        </h3>
        <div className="space-y-3" data-id="element-2980">
          {playMix.map((play, index) => <div key={index} data-id="element-2981">
              <div className="flex items-center justify-between mb-2" data-id="element-2982">
                <span className="text-sm text-gray-700" data-id="element-2983">{play.name}</span>
                <div className="text-right" data-id="element-2984">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-2985">
                    {play.allocation}%
                  </span>
                  <span className="text-xs text-gray-600 ml-2" data-id="element-2986">
                    {formatCurrency(play.expectedRevenue)}
                  </span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2987">
                <div className={`${play.color} h-2 rounded-full transition-all`} style={{
              width: `${play.allocation}%`
            }} data-id="element-2988" />
              </div>
            </div>)}
        </div>
      </div>
      {/* Budget & Resources Grid */}
      <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-2989">
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-2990">
          <div className="flex items-center gap-2 mb-2" data-id="element-2991">
            <DollarSignIcon className="w-4 h-4 text-blue-600" data-id="element-2992" />
            <span className="text-xs font-medium text-blue-900" data-id="element-2993">
              Budget Allocation
            </span>
          </div>
          <p className="text-2xl font-bold text-blue-900" data-id="element-2994">$850K</p>
          <p className="text-xs text-blue-700 mt-1" data-id="element-2995">8.5% of revenue target</p>
        </div>
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200" data-id="element-2996">
          <div className="flex items-center gap-2 mb-2" data-id="element-2997">
            <UsersIcon className="w-4 h-4 text-purple-600" data-id="element-2998" />
            <span className="text-xs font-medium text-purple-900" data-id="element-2999">
              Headcount Model
            </span>
          </div>
          <p className="text-2xl font-bold text-purple-900" data-id="element-3000">12 FTEs</p>
          <p className="text-xs text-purple-700 mt-1" data-id="element-3001">5 SDRs, 4 AEs, 3 Ops</p>
        </div>
        <div className="p-4 bg-green-50 rounded-lg border border-green-200" data-id="element-3002">
          <div className="flex items-center gap-2 mb-2" data-id="element-3003">
            <CalendarIcon className="w-4 h-4 text-green-600" data-id="element-3004" />
            <span className="text-xs font-medium text-green-900" data-id="element-3005">Timeline</span>
          </div>
          <p className="text-2xl font-bold text-green-900" data-id="element-3006">12 mo</p>
          <p className="text-xs text-green-700 mt-1" data-id="element-3007">Full year plan</p>
        </div>
      </div>
      {/* Execution Timeline */}
      <div data-id="element-3008">
        <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-3009">
          Execution Timeline
        </h3>
        <div className="space-y-2" data-id="element-3010">
          <div className="flex items-center gap-3" data-id="element-3011">
            <div className="w-20 text-xs font-medium text-gray-600" data-id="element-3012">
              Q1 2024
            </div>
            <div className="flex-1 bg-gray-100 rounded-full h-2" data-id="element-3013">
              <div className="bg-blue-500 h-2 rounded-full w-3/4" data-id="element-3014" />
            </div>
            <span className="text-xs text-gray-600" data-id="element-3015">Setup & Launch</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-3016">
            <div className="w-20 text-xs font-medium text-gray-600" data-id="element-3017">
              Q2 2024
            </div>
            <div className="flex-1 bg-gray-100 rounded-full h-2" data-id="element-3018">
              <div className="bg-purple-500 h-2 rounded-full w-full" data-id="element-3019" />
            </div>
            <span className="text-xs text-gray-600" data-id="element-3020">Scale & Optimize</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-3021">
            <div className="w-20 text-xs font-medium text-gray-600" data-id="element-3022">
              Q3 2024
            </div>
            <div className="flex-1 bg-gray-100 rounded-full h-2" data-id="element-3023">
              <div className="bg-green-500 h-2 rounded-full w-full" data-id="element-3024" />
            </div>
            <span className="text-xs text-gray-600" data-id="element-3025">Peak Performance</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-3026">
            <div className="w-20 text-xs font-medium text-gray-600" data-id="element-3027">
              Q4 2024
            </div>
            <div className="flex-1 bg-gray-100 rounded-full h-2" data-id="element-3028">
              <div className="bg-orange-500 h-2 rounded-full w-5/6" data-id="element-3029" />
            </div>
            <span className="text-xs text-gray-600" data-id="element-3030">Year-End Push</span>
          </div>
        </div>
      </div>
    </div>;
}